import {
  WBBox,
  WBFlex,
  WBIcon,
  WBIconButton,
  WBLinkButton,
  WBSideNav,
  WBView,
} from '@admiin-com/ds-web';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router-dom';
import { PATHS } from '../../../navigation/paths';
import { useMediaQuery, useTheme } from '@mui/material';
import MainLayout, {
  MainLayoutGridType,
} from '../../../components/MainLayout/MainLayout';
// import { usePaymentEnabled } from '../../../hooks/usePaymentEnabled/usePaymentEnabled';
import { useMemo } from 'react';
import { Entity } from '@admiin-com/ds-graphql';
import { useLogout } from '../../hooks/useLogout/useLogout';

export interface SettingsProps {
  isClientContext?: boolean;
  clientId?: string;
  onBack: () => void;
  customPaths?: Array<{
    to: string;
    title: string;
    subTitle: string;
    icon: string;
    target: string;
  }>;
  renderContent?: (section: string) => React.ReactNode; // Add this prop
  activeSection?: string | null; // Add this prop
  selectedEntity: Entity | null;
}

const Settings = ({
  isClientContext = false,
  clientId,
  onBack,
  customPaths,
  renderContent,
  activeSection,
  selectedEntity,
}: SettingsProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  //const paymentEnabled = usePaymentEnabled();
  const paymentEnabled = true; //TODO: need to retrieve from client entity user
  const theme = useTheme();
  const downLg = useMediaQuery(theme.breakpoints.down('lg'));

  // Default paths for global settings
  const defaultPaths = useMemo(
    () => [
      {
        to: PATHS.account,
        title: t('accountTitle', { ns: 'common' }),
        subTitle: t('accountSubTitle', { ns: 'common' }),
        icon: 'ChevronForward',
        target: '_self',
      },
      {
        to: PATHS.business,
        title: t('businessTitle', { ns: 'common' }),
        subTitle: t('businessSubTitle', {
          ns: 'common',
          business: selectedEntity?.name,
        }),
        icon: 'ChevronForward',
        target: '_self',
      },
      ...(paymentEnabled
        ? [
            {
              to: PATHS.paymentMethods,
              title: t('paymentTitle', { ns: 'common' }),
              subTitle: t('paymentSubTitle', { ns: 'common' }),
              icon: 'ChevronForward',
              target: '_self',
            },
            {
              to: PATHS.receivingAccounts,
              title: t('receivingAccountTitle', { ns: 'common' }),
              subTitle: t('receivingAccountSubTitle', { ns: 'common' }),
              icon: 'ChevronForward',
              target: '_self',
            },
            {
              to: PATHS.transactions,
              title: t('transactionsTitle', { ns: 'common' }),
              subTitle: t('transactionsSubTitle', { ns: 'common' }),
              icon: 'ChevronForward',
              target: '_self',
            },
          ]
        : []),
      {
        to: PATHS.users,
        title: t('usersTitle', { ns: 'common' }),
        subTitle: t('usersSubTitle', { ns: 'common' }),
        icon: 'ChevronForward',
        target: '_self',
      },
      {
        to: PATHS.support,
        title: t('supportTitle', { ns: 'common' }),
        subTitle: t('supportSubTitle', { ns: 'common' }),
        icon: 'Open',
        target: '_self',
      },
    ],
    [t, selectedEntity, paymentEnabled]
  );

  // Use custom paths if provided (for client context), otherwise use default
  const paths = customPaths || defaultPaths;

  // Determine if we're at the base settings path
  const isSettings = !activeSection;

  let gridType: MainLayoutGridType = 'All';
  if (downLg) {
    if (isSettings) gridType = 'Left';
    else gridType = 'Right';
  }

  // Handle back navigation based on context
  const handleBackClick = () => {
    onBack();
  };
  const logOut = useLogout();

  return (
    <MainLayout
      fullWidth
      gridType={gridType}
      toolbarComponent={
        <WBFlex
          sx={{
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: [0],
            height: '100%',
            overflowY: 'auto',
          }}
        >
          <WBView>
            <WBSideNav paths={paths} />
            <WBBox>
              {!isClientContext && (
                <WBLinkButton
                  onClick={logOut}
                  sx={{ fontSize: 'h2.fontSize', color: 'error.main' }}
                >
                  {t('logOut', { ns: 'common' })}
                </WBLinkButton>
              )}
              <WBBox sx={{ mb: 3 }} />
            </WBBox>
          </WBView>
        </WBFlex>
      }
    >
      <WBView
        position={'relative'}
        maxHeight={isClientContext ? undefined : '100%'}
      >
        {downLg ? (
          <WBFlex
            justifyContent="space-between"
            mt={4}
            ml={{ xs: 2, sm: 4, md: 7 }}
            sx={{ position: 'absolute', zIndex: 100 }}
          >
            <WBIconButton name="ArrowBack" onClick={handleBackClick}>
              <WBIcon
                name="ArrowBack"
                color={
                  pathname === PATHS.paymentMethods
                    ? 'white'
                    : theme.palette.text.primary
                }
                size={'small'}
              />
            </WBIconButton>
          </WBFlex>
        ) : null}
        {renderContent && activeSection ? (
          renderContent(activeSection)
        ) : (
          <Outlet />
        )}
      </WBView>
    </MainLayout>
  );
};

export default Settings;
